import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Card } from '@design-system';
import { Loader } from 'lucide-react';
import { RateActionLetterTemplate } from '@shared';
import { PolicyExtractorProgressBar } from '@client/components/PolicyExtractorProgressBar';
import { selectClient } from '@client/model';
import { LetterSwitcher } from './LetterSwitcher';
import { RateActionPageHeader } from './RateActionPageHeader';
import { RateActionSummary } from './RateActionSummary';
import { RateActionUploadForm } from './RateActionUploadForm';
import { useAutoSelectMostRecentLetter } from './hooks/useAutoSelectMostRecentLetter';
import { useNavigateOnExtractionStart } from './hooks/useNavigateOnExtractionStart';
import {
  useRateActionExtract,
  type RateActionUploadStatus,
} from './hooks/useRateActionExtract';
import { useRateActionExtractRequest } from './hooks/useRateActionExtractRequest';
import { useRateActionExtractRequests } from './hooks/useRateActionExtractRequests';

interface SetActiveRequestOptions {
  replace?: boolean;
  /** Pin the user on the upload screen even when prior letters exist (?upload=1). */
  showUpload?: boolean;
}

export function RateActionLetterContent() {
  const client = useSelector(selectClient);
  const clientId = client?.clientId;
  const [searchParams, setSearchParams] = useSearchParams();
  const activeRequestId = searchParams.get('requestId');
  const showUpload = searchParams.get('upload') === '1';

  const priorLettersQuery = useRateActionExtractRequests(clientId);
  // Exclude in-flight extractions from the picker — they have no metadata to display and
  // selecting one would land the user on a polling panel rather than a usable comparison.
  // The upload flow drives the user directly to the new requestId so they still see progress.
  const priorLetters = (priorLettersQuery.data ?? []).filter(l => l.status !== 'processing');
  const hasPriorLetters = priorLetters.length > 0;

  const activeLetterQuery = useRateActionExtractRequest(activeRequestId ?? undefined);

  const setActiveRequest = useCallback(
    (requestId: string | null, options?: SetActiveRequestOptions) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          if (requestId) {
            next.set('requestId', requestId);
            next.delete('upload');
          } else {
            next.delete('requestId');
            if (options?.showUpload) next.set('upload', '1');
            else next.delete('upload');
          }
          return next;
        },
        { replace: options?.replace ?? false },
      );
    },
    [setSearchParams],
  );

  // Extraction lifecycle lives at this level so the progress bar can render in a stable
  // JSX slot — the same component instance stays mounted across upload phases, the URL
  // change, and server-side polling, instead of being unmounted/remounted when the form
  // is swapped for the loading panel.
  const extract = useRateActionExtract({
    clientId: clientId ?? '',
    letterTemplate: RateActionLetterTemplate.GENWORTH_7000K,
  });

  useNavigateOnExtractionStart({
    status: extract.status,
    requestId: extract.requestId,
    onStart: useCallback(
      (id: string) => setActiveRequest(id, { replace: true }),
      [setActiveRequest],
    ),
  });

  // Server orders by createdDateTime DESC, so the first matching row is most recent.
  // Skip both processing (still extracting) and error (no usable results) — auto-select
  // should land the user on something they can actually compare against.
  const mostRecentCompletedRequestId = priorLetters.find(
    l => l.status === 'completed' || l.status === 'applied',
  )?.requestId;
  useAutoSelectMostRecentLetter({
    isReady: priorLettersQuery.isSuccess,
    activeRequestId,
    showUpload,
    mostRecentRequestId: mostRecentCompletedRequestId,
    onSelect: useCallback(
      (id: string) => setActiveRequest(id, { replace: true }),
      [setActiveRequest],
    ),
  });

  // Bar is shown while EITHER the client-side mutation is in flight OR the server says the
  // active letter is still being extracted. Stays true continuously from upload-start
  // through extraction-complete, keeping the bar mounted across the URL change.
  const isExtracting =
    isExtractInFlight(extract.status) || activeLetterQuery.data?.status === 'processing';

  // True between the list resolving and the auto-select effect firing — keeps the loader
  // up so the page doesn't flicker to the upload form for one render.
  const willAutoSelect =
    priorLettersQuery.isSuccess &&
    !activeRequestId &&
    !showUpload &&
    mostRecentCompletedRequestId != null;

  // Non-extraction fetch states (fast paths): list query, about-to-auto-select, detail
  // fetch on a non-processing letter. These get a simple spinner since the duration is
  // not knowable up-front.
  const isFetchingNonExtraction =
    !isExtracting &&
    (priorLettersQuery.isPending ||
      willAutoSelect ||
      (activeRequestId != null && activeLetterQuery.isLoading));

  if (!clientId) {
    return (
      <div className="mx-auto max-w-2xl p-6">
        <Card className="p-6">
          <p className="text-sm text-gray-700">Loading client…</p>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <RateActionPageHeader
        clientId={clientId}
        title="Rate Action Letters"
        subtitle="Upload a premium increase notice to compare coverage change options against your current benefits."
      />

      <div className="mx-auto max-w-5xl space-y-6 p-6">
        {hasPriorLetters && !isExtracting && (
          <LetterSwitcher
            letters={priorLetters}
            activeRequestId={activeRequestId}
            onSelect={setActiveRequest}
            onUploadNew={() => setActiveRequest(null, { showUpload: true })}
          />
        )}

        {isExtracting ? (
          <Card className="space-y-2 p-6">
            <PolicyExtractorProgressBar />
            <p className="text-center text-xs text-gray-600">
              {extractionStatusLabel(extract.status, activeLetterQuery.data?.status)}
            </p>
          </Card>
        ) : isFetchingNonExtraction ? (
          <Card className="flex items-center gap-2 p-6 text-sm text-gray-700">
            <Loader className="size-4 animate-spin" /> Loading letter…
          </Card>
        ) : activeRequestId ? (
          <ActiveLetterPanel
            requestId={activeRequestId}
            clientId={clientId}
            data={activeLetterQuery.data}
            error={activeLetterQuery.error as Error | null}
          />
        ) : (
          <RateActionUploadForm
            onSubmit={file => extract.startUpload(file)}
            errorMessage={extract.errorMessage}
          />
        )}
      </div>
    </div>
  );
}

function isExtractInFlight(status: RateActionUploadStatus): boolean {
  return status !== 'idle' && status !== 'error' && status !== 'completed';
}

function extractionStatusLabel(
  extractStatus: RateActionUploadStatus,
  serverStatus: string | undefined,
): string {
  switch (extractStatus) {
    case 'requestingUrl':
      return 'Preparing upload…';
    case 'uploadingFile':
      return 'Uploading…';
    case 'enqueueing':
      return 'Queueing extraction…';
    case 'polling':
      return 'Extracting (this may take a minute)…';
    default:
      // Covers refresh-resume (no in-session mutation) and any other path where the
      // server tells us extraction is still running.
      return serverStatus === 'processing'
        ? 'Extracting (this may take a minute)…'
        : 'Working…';
  }
}

interface ActiveLetterPanelProps {
  requestId: string;
  clientId: string;
  data: ReturnType<typeof useRateActionExtractRequest>['data'];
  error: Error | null;
}

// Loading and processing states are handled at the page level (single combined spinner).
// This panel only renders terminal states: fetch error, missing record, extraction error,
// or the successful summary.
function ActiveLetterPanel({ requestId, clientId, data, error }: ActiveLetterPanelProps) {
  if (error) {
    return (
      <Card className="border-red-200 bg-red-50 p-4">
        <p className="text-sm text-red-700">
          Could not load this letter: {error.message || 'Unknown error'}
        </p>
      </Card>
    );
  }
  if (!data) {
    return (
      <Card className="p-6">
        <p className="text-sm text-gray-700">Letter not found.</p>
      </Card>
    );
  }
  if (data.status === 'error') {
    return (
      <Card className="border-red-200 bg-red-50 p-4">
        <p className="text-sm text-red-700">
          Extraction failed: {data.error ?? 'Unknown error'}
        </p>
      </Card>
    );
  }
  if (!data.results) {
    return (
      <Card className="p-6">
        <p className="text-sm text-gray-700">No extraction results available.</p>
      </Card>
    );
  }
  return (
    <RateActionSummary
      resultsJson={data.results}
      requestId={requestId}
      clientId={clientId}
      appliedFundingSourceIds={data.appliedFundingSourceIds}
    />
  );
}
